<template>
  <div class="project">

    <nav class="navbar is-mobile is-medium" style="background-color: #30008A;" role="navigation" aria-label="main navigation">
      <div class="navbar-brand">
          <img id="navbar-logo" src="../../assets/projects/fflowers/glow19.jpg" alt="Solid Logo">
      </div>
    </nav>

    <section>
          <h1>Frightened Flowers: A Living Art Installation</h1>
          <p>
            "Frightened Flowers" is a mesmerizing blend of biology, technology, and art. It offers a unique insight into the emotions of plants.
          </p>
        
    </section>
    <br>
    <video class="vid" src="../../assets/projects/fflowers/vid.mp4" muted controls autoplay></video>

    <!-- Introduction -->
    <section>
          <h2> Introduction</h2>
          <p>
            Fontys ICT and its excellence program proactively prepare for GLOW, involving two groups shaping concepts and an extra team scaling them to products. In my initial GLOW project last year, I focused on product development. In 2019, I concentrated on concept creation and a test setup, utilizing stepper motors, LED strips, microcomputers, and distance sensors for our installation.
          </p>
          <p>
            These elements seamlessly integrated into wooden frames, with microcomputers enabling communication and transmitting animated pulses. Each plant boasted a distinct color and frame, crafting a mesmerizing visual spectacle. Throughout this endeavor, my role predominantly encompassed concept creation, crafting small prototypes, and programming intricate LED light effects.
          </p>
          <br>
          <figure>
            <img src="../../assets/projects/fflowers/svgdrawing.jpg" alt="Concept Art: Frightened Flowers" class="image" />
            <figcaption>Concept Art: Frightened Flowers</figcaption>
          </figure>
    </section>

    <!-- Frame Design -->
    <section>
          <h2 >Frame Research and Design</h2>
          <p>
            For Glow 2019, we embarked on creating "Frightened Flowers." The idea revolved around frames that house plants with the unique ability to communicate through light. Vincent Venhuizen led the frame research, meticulously designing structures that seamlessly blended nature and technology. These frames served as the canvas for our living art installation.
          </p> <br>
          <figure> 
            <img src="../../assets/projects/fflowers/interactive-art-installation.jpg" alt="Frame Design for Frightened Flowers" class="image" />
            <figcaption>Frame Design for Frightened Flowers</figcaption>
          </figure>
    </section>

    <!-- Sensor Research -->
    <section>
          <h2 >Sensor Research and Selection</h2>
          <p>
            To bring "Frightened Flowers" to life, Geert Boer delved into the world of sensors. The goal was to make the installation interactive, allowing viewers to witness the plants' emotions. After comprehensive research, Geert selected ultrasonic sensors to detect human presence, a pivotal element in the project's interactive nature. He addressed questions about motor types and concluded that AC stepper motors were ideal due to their precision and control.
          </p>
          <figure>
            <img src="../../assets/projects/fflowers/sensor-selection.jpg" alt="Sensor Selection Process" class="image" />
            <figcaption>Sensor Selection Process</figcaption>
          </figure>
    </section>

    <!-- Pot Research and Design -->
    <section>
          <h2 >Pot Research and Design</h2>
          <p>
            Yevheniia Buzykina took on the challenge of choosing the right pots for our project. Transparency was key, as it allowed viewers to see the LED strips within, which served as the roots of our "Frightened Flowers." After considering various options, we decided that transparent plastic pots were the perfect choice. These pots offered not only transparency but also durability and affordability. The decision to use fake flowers complemented our choice, providing stability and aesthetics. They allowed us to harmoniously blend technology with nature. Our commitment to making every detail count was evident as we contemplated the final appearance of the flowers. Should they be real, 3D-printed, or entirely fake? The choice was clear - fake flowers ensured stability, offered room for the necessary hardware, and guaranteed an enduring and awe-inspiring experience.
          </p>
          <figure>
            <div class="columns">
              <div class="column">
                <img src="../../assets/projects/fflowers/potoff.png" alt="Pot Design for Frightened Flowers" class="image" />
                <figcaption>Plant pot with no communication</figcaption>
              </div>
              <div class="column">
                <img src="../../assets/projects/fflowers/poton.png" alt="Pot Design for Frightened Flowers" class="image" />
                <figcaption>Plant pot with communication between multiple pots</figcaption>
              </div>
            </div>
          </figure>
    </section>

    <!-- Hardware and Software Integration -->
    <section>
          <h2>Hardware and Software Integration</h2>
          <p>
            The heart of our project lay in the harmonious integration of hardware and software. Arduino played a pivotal role in orchestrating the dance of "Frightened Flowers." It controlled the LED strips inside the pots, allowing them to paint vibrant emotions with radiant light. The team meticulously crafted a master-slave communication system to ensure the plants' synchronized reactions to human presence.
          </p>
          <figure>
            <br>
            <div class="container" style="background: #9b79da;">
              <div class="columns is-multiline">
                  <div class="column is-full">
                      <div class="columns is-multiline">
                          <div class="column is-full">
                              <img src="../../assets/projects/fflowers/uno.png" alt="Hardware and Software Integration"  />
                          </div>
                          <div class="column is-full">
                              <img src="../../assets/projects/fflowers/mega.png" alt="Hardware and Software Integration" />
                          </div>
                          <div class="column is-full" style="text-align: center;" >
                              <figcaption>Hardware and Software Integration</figcaption>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
          </figure>
    </section>

    <!-- Interactive Art Installation -->
    <section>
          <h2 >Interactive Art Installation</h2>
          <p>
            The result is an interactive art installation where plants become the artists, painting their emotions with radiant light. As viewers approach, the plants' fear is palpable through their vibrant communication. "Frightened Flowers" stands as a testament to nature's fragility and its intrinsic connection with humans.
          </p> <br>
          <figure>

            <br>
            <div class="container">
              <div class="columns is-multiline">
                  <div class="column is-full">
                      <div class="columns is-multiline">
                          <div class="column is-full">
                              <img src="../../assets/projects/fflowers/interactive-art-installation.jpg" alt="Hardware and Software Integration"  />
                          </div>
                          <div class="column is-full" style="text-align: center;" >
                              <figcaption>Interactive Art Installation: Frightened Flowers</figcaption>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
          </figure>
    </section>
  </div>
</template>

<style scoped>
  
@import 'project.css';

</style>